import {
    ChangeDetectionStrategy,
    Component,
    ComponentRef,
    createComponent,
    createEnvironmentInjector,
    DestroyRef,
    ElementRef,
    EnvironmentInjector,
    inject,
    Injector,
    input,
    InputSignal,
    OnInit,
    Signal,
    signal,
    viewChild,
    ViewContainerRef,
    WritableSignal
} from "@angular/core";
import {onEach} from '../../utils/observable-utils';
import {NavigationService} from "../navigation.service";
import {Page, PageComponentContainer, PageSpec} from "../page";
import {LoadingStatus} from '../../session/loading-status';
import {PageComponentContainerComponent} from './page-component-container/page-component-container.component';
import {SessionModel} from '../../session/session.model';

@Component({
    selector: "woa-page-container",
    templateUrl: "./page-container.component.html",
    styleUrl: "./page-container.component.scss",
    host: {
        "[class.hidden]": "sessionModel.initialLoadStatus() === LoadingStatus.LOADING"
    },
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageContainerComponent implements OnInit {
    public sessionModel: SessionModel = inject(SessionModel);
    private destroyRef: DestroyRef = inject(DestroyRef);
    private element: ElementRef = inject(ElementRef);
    private navigationService: NavigationService = inject(NavigationService);
    private injector: Injector = inject(Injector);

    LoadingStatus = LoadingStatus;

    public readonly rootSelector: InputSignal<string> = input.required<string>();

    private readonly viewContainerRef: Signal<ViewContainerRef> = viewChild.required("content", {read: ViewContainerRef});

    public open: WritableSignal<boolean> = signal(false);

    async ngOnInit(): Promise<void> {
        onEach(this.navigationService.pageCreated$, (pageSpec: PageSpec) => {
            // Create a container component for each page component.
            // This is required to allow the swipable behaviour to be attached.
            // Inject the page spec into the container. This gives the container access to the page instance and the specified geometry.
            const containerComponentRef: ComponentRef<PageComponentContainer> = createComponent(PageComponentContainerComponent, {
                environmentInjector: createEnvironmentInjector([{
                    provide: PageSpec,
                    useValue: pageSpec
                }], this.injector.get(EnvironmentInjector))
            });

            // Inject the container component ref into the page instance.
            // We use this to close the container component based on dismissal of the page instance.
            // It also gives the page access to the page spec including the geometry (e.g. the nav menu places its close button based on the side of the page container).
            pageSpec.componentRef.instance.containerComponentRef = containerComponentRef;

            // Add the container component to this container
            this.viewContainerRef().insert(containerComponentRef.hostView);
            containerComponentRef.location.nativeElement.style.zIndex = this.viewContainerRef().length;

            // Start the view open transition after it has been initialised
            setTimeout(() => {
                this.open.set(true);
                containerComponentRef.instance.openPageComponent();
            })
        }, this.destroyRef);

        onEach(this.navigationService.pageDismissed$, (pageComponentRef: ComponentRef<Page>) => {
            const containerComponentRef: ComponentRef<PageComponentContainer> = pageComponentRef.instance.containerComponentRef;
            if (!containerComponentRef) {
                return;
            }

            // Close the page container if removing the last page component (start the close transition)
            if (this.viewContainerRef().length == 1) {
                this.open.set(false);
            }

            // Close the component container (start the close transition)
            containerComponentRef.instance.closePageComponent();

            // Destroy the component container AFTER its close transition has had time to complete
            const period: string = getComputedStyle(this.element.nativeElement).getPropertyValue("--woa-page-transition-period").trim();
            const transitionPeriod: number = period.endsWith("ms") ? parseInt(period.replace("ms", ""))
                : period.endsWith("s") ? parseInt(period.replace("s", "")) / 1000
                    : 300;
            setTimeout(() => containerComponentRef.destroy(), transitionPeriod * 2)
        }, this.destroyRef);

        // Create the initial root page
        if (this.rootSelector()) {
            return this.navigationService.createPage(this.rootSelector(), undefined, undefined, true);
        }
    }
}
